import styled from '@emotion/styled';
import { ResizeObserver } from '@juggle/resize-observer';
import { Divider, ExpandArrow, sronly, typography } from '@landsbankinn/ui';
import get from 'lodash.get';
import React, { useState } from 'react';
import { animated, useSpring } from 'react-spring';
import useMeasure from 'react-use-measure';
import type { ImgSource, ImgAr } from '../../components/img/Img';
import { Img } from '../../components/img/Img';

const CardHost = styled(animated.div)<any>`
  position: relative;
  width: 100%;
  height: ${(props) =>
    props['data-collapsed'] ? props['data-wrapheight'] : '100%'};
  overflow: ${(props) => (props['data-collapsed'] ? 'hidden' : 'vidible')};

  background-color: ${(props) =>
    props['data-backgroundalt']
      ? props.theme.colors.backgroundAlt
      : props.theme.colors.background};
`;

const Content = styled.div``;

const Body = styled.div`
  padding: 64px 36px 36px;
`;

const Title = styled.h2`
  ${typography.h4};
  margin-bottom: 3px;
`;

const Copy = styled.div`
  ${typography.body}
  margin-bottom: 24px;
`;

const ExpandBtn = styled(animated.button)`
  position: absolute;
  bottom: 0;
  right: 1px;
  left: 1px;

  height: 125px;
  width: calc(100% - 2px);
  overflow: visible;

  cursor: pointer;
  background-image: linear-gradient(
    0deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 1) 30%,
    rgba(255, 255, 255, 0) 100%
  );

  svg {
    position: absolute;
    left: calc(50% - 11px);
    bottom: 25px;
  }
`;

const SrOnly = styled.span`
  ${sronly}
`;

export interface CardProps {
  title: React.ReactNode;
  description: React.ReactNode;
  image?: ImgSource;
  ar?: ImgAr;
  backgroundAlt?: boolean;
  imageRef?: any;
  ref?: any;
  isCollapsed?: boolean;
  index?: number;
  [key: string]: any;
}

export function MoreInfoCard(props: CardProps) {
  const {
    title,
    description,
    image,
    ar,
    backgroundAlt = false,
    imageRef,
    isCollapsed,
    index,
    ...rest
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(isCollapsed ? false : true);
  const [copyRef, copySize] = useMeasure({ polyfill: ResizeObserver });
  const hasImage = !!image;
  const wrapHeight =
    hasImage && isCollapsed ? '670px' : !isCollapsed ? '100%' : '390px';

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const setHeight = useSpring({
    height:
      isOpen && isCollapsed
        ? `${(get(copySize, 'height', 0) as number) + 60}px`
        : !isCollapsed
        ? '100%'
        : wrapHeight,
  });

  const fadeOut = useSpring({
    backgroundImage: isOpen
      ? `linear-gradient(
      0deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0) 30%,
      rgba(255, 255, 255, 0) 100%
    )`
      : `linear-gradient(
      0deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 1) 40%,
      rgba(255, 255, 255, 0) 100%
    )`,
  });

  return (
    <CardHost
      data-backgroundalt={backgroundAlt}
      data-collapsed={isCollapsed}
      style={{ ...setHeight } as any}
      id={`more-info-${index}`}
      data-wrapheight={wrapHeight}
      {...rest}
    >
      <Content ref={copyRef}>
        {!!image && (
          <Img src={image} ar={ar} ref={imageRef} percentage={0.33} />
        )}
        <Body>
          <Title>{title}</Title>
          <Divider margin="32px 0 36px" />
          <Copy>{description}</Copy>
        </Body>
        {!!isCollapsed && (
          <ExpandBtn
            onClick={toggle}
            role="button"
            aria-expanded={isOpen ? true : false}
            aria-controls={`more-info-${index}`}
            style={{ ...fadeOut } as any}
          >
            <ExpandArrow isOpen={isOpen} />
            <SrOnly>See more</SrOnly>
          </ExpandBtn>
        )}
      </Content>
    </CardHost>
  );
}
